import { t, Trans } from '@lingui/macro'
import { Add, Delete, Download } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Icon,
  Link,
  TextField,
  Typography
} from '@mui/material'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { dateFormat } from 'app/appSettings'
import { queryDeleteObject, queryUpdateObject } from 'app/backend/Common'
import {
  addNewAction,
  changeAction,
  moveToColumn,
  removeAction,
  removeAllActions,
  setInitialState
} from 'app/redux/actions/ActionPlanActions'
import {
  createAction,
  getActions, INTERNAL_USER, updateActionsOrder
} from 'app/services/sfAuth/sfData/sfActionPlan'
import { getActionTemplatesFull } from 'app/services/sfAuth/sfData/sfActionTemplates'
import { findSurveyTemplatesWithChildren } from 'app/services/sfAuth/sfData/sfSurvey'
import { ButtonIcon } from 'app/views/common-components/ButtonIcon'
import { CustomBackButton } from 'app/views/common-components/CustomButton'
import { DialogTitleWithIconClose } from 'app/views/common-components/DialogTitleWithIconClose'
import { FormTopLabel } from 'app/views/common-components/forms/FormTopLabel'
import { PortalTabHeader } from 'app/views/common/PortalTabHeader'
import { GRANTS_PATH } from 'app/views/grants/GrantsConstants'
import { SAT_PATHS } from 'app/views/self-assessment/SATConstants'
import Loading from 'egret/components/EgretLoadable/Loading'
import * as Excel from 'exceljs'
import { saveAs } from 'file-saver'
import _ from 'lodash'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { DragDropContext } from 'react-beautiful-dnd'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { myI18n } from 'translation/I18nConnectedProvider'
import sfOauthConfig from '../../../services/sfAuth/sfAuthConfig'
import ActionDoneDialog from './ActionDoneDialog'
import './ActionPlan.scss'
import ActionPlanColumn from './ActionPlanColumn'
import ActionPlanToPrintPdf from './ActionPlanToPrintPdf'
import AddOrEditActionDialog from './AddOrEditActionDialog'
import ViewAllActionsButton from './ViewAllActionsButtons'

export const columnsInOrder = ['Selected', 'In Progress', 'Done', 'On hold']
export const columnsTranslations = [
  t`Selected`,
  t`In Progress`,
  t`Done`,
  t`On hold`
]
export const INDEX_OF_SELECTED = 0
export const INDEX_OF_IN_PROGRESS = 1
export const INDEX_OF_DONE = 2
export const INDEX_OF_FAILED = 3

const exportActionPlan = ({ data, myI18n }) => {
  const workbook = new Excel.Workbook()
  const downloadDate = moment.utc().format(dateFormat)

  const filename = myI18n._(t`ActionPlan-${downloadDate}`)

  const sheet = workbook.addWorksheet(filename, {
    // views: [{ state: 'frozen', xSplit: 2, ySplit: 1 }]
  })
  const mapActionStatus = column => {
    if (column === 'Selected') {
      return t`[APExcel]Not Started`
    } else if (column === 'Done') {
      return t`[APExcel]Completed`
    } else {
      return '[APExcel]' + column
    }
  }

  const mapCategory = category => {
    if (category === 'Property Management') {
      return t`Asset Management`
    } else if (
      category === 'Financial Managment' ||
      category === 'Financial Management'
    ) {
      return t`Financial Viability`
    } else if (category === 'Governance') {
      return t`Governance`
    } else if (category === 'Social and community relations') {
      return t`Social Inclusion`
    } else {
      return t`Other`
    }
  }

  sheet.columns = [
    // { header: myI18n._(t`Client Program`), width: 25, key: 'clientProgram' }, // blank
    // { header: myI18n._(t`Organization`), width: 25, key: 'organization' }, // blank
    { header: myI18n._(t`Name`), width: 25, key: 'name' }, // Action Name (English version, or whatever the client enters if it’s a manually created action)
    {
      header: myI18n._(t`Observation Category`),
      width: 25,
      key: 'observationCategory'
    }, // Category - Property management should be exported as "Asset Management" , Financial management should be "Financial Viability", Social and Community Relations should be "Social Inclusion", None should be "Other", and Governance is fine written as Governance
    { header: myI18n._(t`Description`), width: 25, key: 'description' }, // Should be the actions's Description (EN) followed by ". Expected outcome:" then the action's Expected outcome (EN).
    { header: myI18n._(t`Observation`), width: 25, key: 'observation' }, // Actual Outcome, if there is one entered by the user on the action plan
    { header: myI18n._(t`Recommendations`), width: 25, key: 'recommendations' }, // blank
    // { header: myI18n._(t`Program`), width: 25, key: 'program' }, // blank
    { header: myI18n._(t`Start Date`), width: 25, key: 'startDate' }, // createdDate or better date of selected
    { header: myI18n._(t`End Date`), width: 25, key: 'endDate' }, // dueDate
    { header: myI18n._(t`Status Reason`), width: 25, key: 'status' }, // Status of the action - Selected should read as "Not started", Done should read "Completed", and On Hold and In progress should stay the same
    { header: myI18n._(t`Created On`), width: 25, key: 'createdOn' } // download date
  ]

  /*
             title: action.Title__c || '',
             id: action.Id,
             order: action.Order__c,
             reason: action.Reason__c,
             dueDate: action.Date__c,
             column: action.Status__c,
             createdDate: action.CreatedDate,
             description: action.Description__c,
             success: action.Success__c,
             expectedOutcome: action.ExpectedOutcome__c,
             actualOutcome: action.ActualOutcome__c,
             failReason: action.ReasonOfFail__c
   */
  for (let i = 0; i < data.columns.length; i++) {
    for (const action of data.columns[i].actions) {
      const { description, expectedOutcome } = action
      sheet.addRow({
        name: action.title,
        sheet,
        observationCategory: myI18n._(mapCategory(action.category)),
        description:
          description +
          '.\n ' +
          myI18n._(t`Expected Outcome`) +
          ': ' +
          expectedOutcome,
        observation: action.actualOutcome,
        startDate: moment.utc(action.createdDate).format(dateFormat),
        endDate: moment.utc(action.dueDate).format(dateFormat),
        status: myI18n._(mapActionStatus(action.column)),
        createdOn: downloadDate
      })
    }
  }
  let rowIndex = 1
  for (rowIndex; rowIndex <= sheet.rowCount; rowIndex++) {
    sheet.getRow(rowIndex).alignment = {
      wrapText: true,
      vertical: 'top'
    }
  }

  const buff = workbook.xlsx.writeBuffer().then(function (data) {
    const blob = new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    saveAs(blob, filename + '.xlsx')
  })
  return buff
}

const columnsData = [
  {
    title: 'Selected',
    actions: []
  },
  {
    title: 'In Progress',
    actions: []
  },
  {
    title: 'Done',
    actions: []
  },
  {
    title: 'On hold',
    actions: []
  }
]

const ActionPlan = () => {
  const [loaded, setLoaded] = useState()
  const [actionTemplates, setActionTemplates] = useState()
  const [surveysIdsMap, setSurveysIdsMap] = useState({})
  const [droppedOnDoneProps, setDroppedOnDoneProps] = useState()
  const [data, setData] = useState(null)
  const [deletingActionProps, setDeletingActionProps] = useState(null)
  const [deletingActionIds, setDeletingActionIds] = useState([])
  const [droppedOnFailedProps, setDroppedOnFailedProps] = useState(null)
  const [failReason, setFailReason] = useState('')
  const [failReasonTouched, setFailReasonTouched] = useState(false)
  const [minimizedColumns, setMinimizedColumns] = useState([3])
  const [shouldOpenDialog, setShouldOpenDialog] = useState(false)
  const [card, setCard] = useState()
  const [create, setCreate] = useState()
  const [isOnHold, setIsOnHold] = useState(null)
  const user = useSelector((state) => state.user)
  const configuration = useSelector((state) => state.configuration)
  const organization = useSelector((state) => state.organization)
  const actionPlan = useSelector((state) => state.actionPlan)
  const dispatch = useDispatch()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    dispatch(setInitialState({ columns: columnsData }))
    setLoaded(false)
    fetchData()
  }, [organization])

  const updateAction = action => {
    return queryUpdateObject({
      type: 'Action',
      updateData: action
    })
  }

  const fetchData = () => {
    const isInternal = sfOauthConfig.isInternal

    Promise.all([
      getActions(
        // if internal override user so the PM can download action Plan
        isInternal ? INTERNAL_USER : user.userId,
        organization.id
      ),
      getActionTemplatesFull(),
      findSurveyTemplatesWithChildren({
        language: user.language
      })
    ]).then(([result, actionTemplates, surveyTemplatesMap]) => {
      const surveysIdsMap = {
        'Co-op': [],
        OSBL: []
      }
      if (configuration.SAT_COOPS_SURVEY) {
        surveysIdsMap['Co-op'] = [
          ...surveyTemplatesMap[configuration.SAT_COOPS_SURVEY].children.map(
            obj => obj.id
          ),
          surveyTemplatesMap[configuration.SAT_COOPS_SURVEY].id
        ]
      }
      if (configuration.SAT_OSBL_SURVEY) {
        surveysIdsMap.OSBL = [
          ...surveyTemplatesMap[configuration.SAT_OSBL_SURVEY].children.map(
            obj => obj.id
          ),
          surveyTemplatesMap[configuration.SAT_OSBL_SURVEY].id
        ]
      }
      if (!organization.id) {
        dispatch(setInitialState({ columns: columnsData }))
        setLoaded(true)
        return
      }
      // const toUpdate = []
      const toSet = _.cloneDeep(columnsData)
      result.forEach(action => {
        const savedAction = {
          title: action.Title__c || '',
          id: action.Id,
          order: action.Order__c,
          reason: action.Reason__c,
          dueDate: action.Date__c,
          column: action.Status__c,
          createdDate: action.CreatedDate,
          description: action.Description__c,
          success: action.Success__c,
          expectedOutcome: action.ExpectedOutcome__c,
          actualOutcome: action.ActualOutcome__c,
          fromTemplate: action.From_Template__c,
          // category: action && action.From_Template__r ? action.From_Template__r.Category__c : '',
          category: action.Category__c,
          failReason: action.ReasonOfFail__c
        }
        // if (!savedAction.category || !savedAction.fromTemplate) {
        //   // Fix for bug resulting in fromTemplate and category not being set
        //   const template =
        //     actionTemplatesMapEn[savedAction.title] ||
        //     actionTemplatesMapFr[savedAction.title]
        //   if (template) {
        //     savedAction.category = template.category
        //     savedAction.fromTemplate = template.id
        //     toUpdate.push({
        //       Id: savedAction.id,
        //       Category__c: savedAction.category,
        //       From_Template__c: savedAction.fromTemplate
        //     })
        //   }
        // }
        let columnIndex = columnsInOrder.indexOf(action.Status__c)
        if (columnIndex === -1) {
          columnIndex = 0
          savedAction.order = -1
          savedAction.column = 'Selected'
        }
        if (columnIndex === -1 && action.Status__c === 'Failed') {
          columnIndex = 3
          savedAction.order = -1
          savedAction.column = 'On hold'
        }
        toSet[columnIndex].actions.push(savedAction)
      })
      // if (toUpdate.length > 0) {
      //   updateActionBug(toUpdate).then(result => {
      //   })
      // }
      toSet.forEach(column => {
        column.actions.sort((a, b) => {
          return a.order - b.order
        })
      })
      dispatch(setInitialState({ columns: toSet }))
      setLoaded(true)
      setActionTemplates(actionTemplates)
      setSurveysIdsMap(surveysIdsMap)
    })
  }

  const onDragEnd = result => {
    const { source, destination } = result
    const columns = [...actionPlan.columns]
    if (!destination) {
      return
    }
    let targetColumnIndex =
      result.targetColumnIndex || result.targetColumnIndex === 0
        ? result.targetColumnIndex
        : null
    let sourceColumnIndex =
      result.sourceColumnIndex || result.sourceColumnIndex === 0
        ? result.sourceColumnIndex
        : null

    columns.forEach((board, index) => {
      if (board.title === destination.droppableId) {
        targetColumnIndex = index
      }
      if (board.title === source.droppableId) {
        sourceColumnIndex = index
      }
    })

    if (
      source.index === destination.index &&
      sourceColumnIndex === targetColumnIndex
    ) {
      return
    }

    if (targetColumnIndex === INDEX_OF_FAILED) {
      setDroppedOnFailedProps({
        source: { ...source, sourceBoardIndex: sourceColumnIndex },
        destination: { ...destination, targetBoardIndex: targetColumnIndex }
      })
      dispatch(moveToColumn({
        movedFromColumn: sourceColumnIndex,
        movedToColumn: targetColumnIndex,
        movedIndex: source.index,
        moveToIndex: destination.index
      }))
      return
    }

    if (targetColumnIndex === INDEX_OF_DONE) {
      setDroppedOnDoneProps({
        source: { ...source, sourceBoardIndex: sourceColumnIndex },
        action: columns[sourceColumnIndex].actions[source.index],
        destination: { ...destination, targetBoardIndex: targetColumnIndex }
      })
      dispatch(moveToColumn({
        movedFromColumn: sourceColumnIndex,
        movedToColumn: targetColumnIndex,
        movedIndex: source.index,
        moveToIndex: destination.index
      }))
      return
    }
    const toMove = { ...columns[sourceColumnIndex].actions[source.index] }
    toMove.column = columnsInOrder[targetColumnIndex]
    toMove.failReason = null
    toMove.success = null
    toMove.actualOutcome = null
    delete toMove.order
    dispatch(moveToColumn({
      movedFromColumn: sourceColumnIndex,
      movedToColumn: targetColumnIndex,
      movedIndex: source.index,
      moveToIndex: destination.index
    }))
    Promise.all([
      reorderColumn(columns, sourceColumnIndex, targetColumnIndex),
      updateAction(toMove)
    ]).then(
      res => {
        enqueueSnackbar(
          <div>
            {toMove.title} <Trans>updated!</Trans>
          </div>,
          {
            variant: 'success'
          }
        )
        dispatch(changeAction({
          actionToChange: toMove,
          columnIndex: targetColumnIndex,
          actionIndex: destination.index
        }))
      },
      reject => {
        enqueueSnackbar(
          <div>
            {toMove.title} <Trans>couldn't be updated!</Trans>
          </div>,
          {
            variant: 'error'
          }
        )
        dispatch(moveToColumn({
          movedFromColumn: targetColumnIndex,
          movedToColumn: sourceColumnIndex,
          movedIndex: destination.index,
          moveToIndex: source.index
        }))
      }
    )
  }

  const reorderColumn = (data, firstIndex, secondIndex = null) => {
    const toUpdate = []
    data[firstIndex].actions.forEach((action, index) => {
      toUpdate.push({ Id: action.id, Order__c: index })
    })
    if (secondIndex && firstIndex !== secondIndex) {
      data[secondIndex].actions.forEach((action, index) => {
        toUpdate.push({ Id: action.id, Order__c: index })
      })
    }
    return updateActionsOrder(toUpdate)
  }

  const handleAddNewCard = createData => {
    const columns = [...actionPlan.columns]
    createAction({
      ...createData,
      column: columns[INDEX_OF_SELECTED].title,
      orgId: organization.id,
      order: columns[INDEX_OF_SELECTED].actions.length
    }).then(result => {
      const newAction = {
        title: createData.title,
        id: result.id,
        category: createData.category,
        order: columns[INDEX_OF_SELECTED].actions.length,
        column: columns[INDEX_OF_SELECTED].title,
        fromTemplate: createData.fromTemplate,
        reason: createData.reason,
        expectedOutcome: createData.expectedOutcome,
        dueDate: createData.dueDate ? createData.dueDate : moment.utc(),
        description: createData.description
      }
      dispatch(addNewAction({ actionToAdd: newAction }))
      enqueueSnackbar(<Trans>Action created!</Trans>, {
        variant: 'success'
      })
    })
  }

  const handleOpenDialog = (card, create = false) => {
    setShouldOpenDialog(true)
    setCard(card)
    setIsOnHold(card.columnIndex === 3)
    setCreate(create)
  }

  const handlePostponeAction = (days, columnIndex, actionIndex) => {
    const toPostpone = {
      ...actionPlan.columns[columnIndex].actions[actionIndex]
    }
    const toModify = moment.utc(toPostpone.dueDate)
    toPostpone.dueDate = toModify.add(days, 'days')
    updateAction(toPostpone).then(
      result => {
        enqueueSnackbar(
          <div>
            {toPostpone.title} <Trans>postponed!</Trans>
          </div>,
          {
            variant: 'success'
          }
        )
        dispatch(changeAction({
          actionToChange: toPostpone,
          columnIndex,
          actionIndex
        }))
      },
      reject => {
        enqueueSnackbar(
          <Trans>Error occured while postponing action!</Trans>,
          {
            variant: 'error'
          }
        )
      }
    )
  }

  const handleDeleteDialogOpen = (card, actionIndex, column) => {
    setDeletingActionProps({
      title: card.title,
      index: actionIndex,
      columnIndex: column
    })
  }

  const handleActionDelete = (actionIndex, deleteFromColumn) => {
    const columnIndex = !isNaN(deleteFromColumn)
      ? deleteFromColumn
      : deletingActionProps.columnIndex
    const toDelete = {
      ...actionPlan.columns[columnIndex].actions[actionIndex]
    }
    setDeletingActionIds([...deletingActionIds, toDelete.id])

    queryDeleteObject({
      type: 'Action',
      ids: toDelete.id
    }).then(
      result => {
        setDeletingActionProps(null)
        setDeletingActionIds((prevIds) => prevIds.filter((id) => id !== toDelete.id))

        enqueueSnackbar(
          <div>
            {toDelete.title} <Trans>deleted!</Trans>
          </div>,
          {
            variant: 'success'
          }
        )
        dispatch(removeAction({
          columnIndex,
          actionIndex
        }))
        reorderColumn(actionPlan.columns, columnIndex)
      },
      reject => {
        setDeletingActionProps(null)
        setDeletingActionIds(prevState =>
          prevState.filter(id => id !== toDelete.id)
        )
        enqueueSnackbar(
          <div>
            <Trans>Error while deleting</Trans> {toDelete.title}!
          </div>,
          {
            variant: 'error'
          }
        )
      }
    )
  }

  const handleActionSave = (
    action,
    columnIndex,
    actionIndex,
    reorderIndex = null
  ) => {
    const actionToSave = { ...action }
    const columns = { ...actionPlan.columns }
    const promises = [updateAction(actionToSave)]
    if (reorderIndex) {
      promises.push(reorderColumn(columns, columnIndex, reorderIndex))
    }
    Promise.all(promises).then(
      result => {
        dispatch(changeAction({
          actionToChange: actionToSave,
          columnIndex,
          actionIndex
        }))
        handleDialogClose()
        enqueueSnackbar(
          <div>
            {actionToSave.title} <Trans>updated!</Trans>
          </div>,
          {
            variant: 'success'
          }
        )
      },
      reject => {
        console.error('reject: ', reject)
        enqueueSnackbar(
          <Trans>Error occured while saving action!</Trans>,
          {
            variant: 'error'
          }
        )
      }
    )
  }

  const handleReasonOnType = event => {
    setFailReason(event.target.value)
  }

  const handleDialogClose = () => {
    setShouldOpenDialog(false)
    setDroppedOnDoneProps(null)
    setDroppedOnFailedProps(null)
  }

  const handleFailedDialogClose = () => {
    if (droppedOnFailedProps) {
      dispatch(moveToColumn({
        movedFromColumn: INDEX_OF_FAILED,
        movedToColumn: droppedOnFailedProps.source.sourceBoardIndex,
        movedIndex: droppedOnFailedProps.destination.index,
        moveToIndex: droppedOnFailedProps.source.index
      }))
    }

    handleDialogClose()
  }

  const handleColumnMinimize = (state, index) => {
    const newColumns = [...minimizedColumns]
    if (newColumns.indexOf(index) !== -1 && !state) {
      newColumns.splice(newColumns.indexOf(index), 1)
    } else {
      newColumns.push(index)
    }
    setMinimizedColumns(newColumns)
  }

  const handleFailReasonBlur = () => {
    setFailReasonTouched(true)
  }

  const columns = actionPlan.columns

  const allActions = []
  columns.forEach(column => {
    column.actions.forEach(action => {
      allActions.push(action)
    })
  })

  const failedAction =
  droppedOnFailedProps
    ? actionPlan.columns[INDEX_OF_FAILED].actions[
      droppedOnFailedProps.destination.index
    ]
    : null

  const isMaxLengthError = failReason.length > 1000
  const isRequiredError = failReasonTouched && failReason.trim().length === 0
  const isError = isMaxLengthError || isRequiredError

  if (!loaded) {
    return <Loading atCenter />
  }

  return (
    <>
      {!organization.id && (
        <div>
          <Alert severity='info'>
            <Trans>
              Before creating actions for an action plan you have to associate
              an organisation with your account
            </Trans>{' '}
            <Link to={GRANTS_PATH.ORGANIZATIONS}>
              <Trans>You can connect to an organization here</Trans>
            </Link>
          </Alert>
        </div>
      )}
      <div
        className='portal-tab-container'
      >
        <PortalTabHeader
          header={
            <Trans>
              ACTION_PLAN
            </Trans>
                }
          description={
            <Trans>
              ACTION_PLAN_INFO
            </Trans>
                }
        />
        {droppedOnFailedProps && (
          <Dialog
            open={Boolean(droppedOnFailedProps)}
            onClose={handleFailedDialogClose}
            maxWidth='xs'
            fullWidth
          >
            <DialogTitleWithIconClose
              label={<Trans>ACTION_PLAN_REASON_FOR_HOLD_TITLE</Trans>}
              handleClose={handleFailedDialogClose}
            />
            <DialogContent sx={{ margin: '0 48px 32px 48px', padding: 0 }}>
              <FormTopLabel><Trans>ACTION_PLAN_ACTION_NAME_LABEL</Trans></FormTopLabel>
              <TextField
                className='text-muted'
                value={failedAction.title}
                disabled
                name='reason'
                variant='filled'
                fullWidth
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    borderRadius: 0,
                    height: 38,
                    backgroundColor: 'rgba(0, 0, 0, 0.10)',
                    border: '1px solid rgba(0, 0, 0, 0.40)',
                    alignItems: 'center'
                  }
                }}
                inputProps={{
                  sx: {
                    padding: '0 12px',
                    height: '100%',
                    boxSizing: 'border-box'
                  }
                }}
                sx={{ mb: '24px' }}
              />
              <FormTopLabel required><Trans>ACTION_PLAN_REASON_FOR_HOLD_LABEL</Trans></FormTopLabel>
              <>
                <TextField
                  className='text-muted'
                  onChange={handleReasonOnType}
                  onBlur={handleFailReasonBlur}
                  name='reason'
                  variant='outlined'
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={10}
                  error={isMaxLengthError || isRequiredError}
                />
                {isRequiredError && (
                  <Box
                    sx={{
                      mt: 1,
                      display: 'inline-flex',
                      width: 'fit-content',
                      padding: '5px 10px',
                      backgroundColor: '#CAE5EF',
                      borderLeft: '3px solid #E51C40'
                    }}
                  >
                    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                      <Trans>This field is required</Trans>
                    </Typography>
                  </Box>
                )}

                {isMaxLengthError && (
                  <Box
                    sx={{
                      mt: 1,
                      display: 'inline-flex',
                      width: 'fit-content',
                      padding: '5px 10px',
                      backgroundColor: '#CAE5EF',
                      borderLeft: '3px solid #E51C40'
                    }}
                  >
                    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                      <Trans>ACTON_PLAN_REASON_FOR_HOLD_ERROR_MESSAGE</Trans>
                    </Typography>
                  </Box>
                )}
              </>
            </DialogContent>
            <DialogActions
              sx={{
                flexDirection: 'column',
                alignItems: 'stretch',
                margin: '0 48px 48px 48px',
                padding: 0,
                gap: 2,
                '& > :not(style) ~ :not(style)': {
                  marginLeft: 0
                }
              }}
            >
              <Button
                fullWidth
                variant='contained'
                disabled={failReason.length < 1 || isError}
                color='primary'
                sx={{
                  minHeight: 40,
                  padding: '8px 0',
                  boxSizing: 'border-box'
                }}
                onClick={() => {
                  const actionIndex = droppedOnFailedProps.destination.index
                  const toUpdate = {
                    ...actionPlan.columns[INDEX_OF_FAILED].actions[
                      actionIndex
                    ]
                  }
                  toUpdate.failReason = failReason
                  toUpdate.success = null
                  toUpdate.actualOutcome = null
                  toUpdate.column = 'On hold'
                  handleActionSave(
                    toUpdate,
                    INDEX_OF_FAILED,
                    actionIndex,
                    droppedOnFailedProps.source.sourceBoardIndex
                  )
                }}
              >
                <Trans>Save</Trans>
              </Button>
              <Button
                fullWidth
                variant='outlined'
                color='primary'
                sx={{
                  minHeight: 40,
                  padding: '8px 0',
                  margin: 0,
                  boxSizing: 'border-box'
                }}
                onClick={handleFailedDialogClose}
              >
                <Trans>Cancel</Trans>
              </Button>
            </DialogActions>
          </Dialog>
        )}
        {droppedOnDoneProps && (
          <ActionDoneDialog
            open={Boolean(droppedOnDoneProps)}
            {...droppedOnDoneProps}
            closeDialog={handleDialogClose}
            handleSave={handleActionSave}
            data={data}
          />
        )}
        {Boolean(deletingActionProps &&
!deletingActionIds.includes(deletingActionProps.id)) && (
  <Dialog open={Boolean(deletingActionProps)}>
    <h4 style={{ padding: 20, textAlign: 'center' }}>
      <Trans>Are you sure you want to delete</Trans>
      <br />
      <b>{deletingActionProps.title}</b>?
    </h4>
    <Grid container direction='row' justifyContent='space-around'>
      <Button
        variant='contained'
        color='primary'
        style={{
          marginBottom: 10
        }}
        onClick={() => {
          handleActionDelete(deletingActionProps.index)
        }}
      >
        <Icon style={{ marginRight: 5 }}>check</Icon>
        <Trans>Yes</Trans>
      </Button>
      <Button
        variant='contained'
        color='primary'
        style={{
          marginBottom: 10
        }}
        onClick={() => {
          setDeletingActionProps(null)
        }}
      >
        <Icon style={{ marginRight: 5 }}>close</Icon>
        <Trans>No</Trans>
      </Button>
    </Grid>
  </Dialog>
        )}
        <Grid container direction='row' justifyContent='space-between' wrap='nowrap' sx={{ marginTop: '48px', marginBottom: '48px' }}>
          <Grid xs container item direction='row' gap='24px' wrap='nowrap'>
            <CustomBackButton
              onClick={e => {
                history.push(SAT_PATHS.SAT_LANDING)
              }}
            >
              <Trans>BACK_TO_OVERVIEW</Trans>
            </CustomBackButton>
            <Button
              disabled={!organization.id}
              variant='contained'
              color='primary'
              style={{
                zIndex: 10
              }}
              onClick={() => {
                handleOpenDialog(null, true)
              }}
            >
              <Trans>ADD_NEW_ACTION</Trans>
              <ButtonIcon IconClass={Add} />
            </Button>
            <ViewAllActionsButton
              handleActionDelete={handleActionDelete}
              handleAddNewCard={handleAddNewCard}
              surveysIdsMap={surveysIdsMap}
              templates={actionTemplates}
              columns={columns}
            />

            {allActions.length > 0 && (
              <DeleteAllActionsButton
                orgId={organization.id}
                handleDeleted={() => {
                  dispatch(removeAllActions())
                }}
              />
            )}
          </Grid>

          <Grid xs container item direction='row' gap='24px' wrap='nowrap' justifyContent='flex-end'>
            <PDFDownloadLink
              document={
                <ActionPlanToPrintPdf columns={columns} i18n={myI18n} />
            }
              fileName={`${myI18n._(t`Action Plan`)}.pdf`}
            >
              {({ blob, url, loading, error }) => (
                <Button variant='outlined' color='primary'>
                  <Trans>ACTION_PLAN_DOWNLOAD_PDF</Trans>
                  <ButtonIcon IconClass={Download} />
                </Button>
              )}
            </PDFDownloadLink>

            <Button
              variant='outlined'
              color='primary'
              onClick={() => {
                exportActionPlan({
                  data: actionPlan,
                  myI18n
                })
              }}
            >
              <Trans>ACTION_PLAN_DOWNLOAD_EXCEL_SHEET</Trans>
              <ButtonIcon IconClass={Download} />
            </Button>

          </Grid>
        </Grid>

        <DragDropContext onDragEnd={onDragEnd}>
          <Grid
            container
            direction='row'
            justifyContent='flex-start'
            style={{ flexGrow: 1 }}
          >
            {columns.map((column, index) => {
              column.index = index
              return (
                <ActionPlanColumn
                  key={index}
                  column={column}
                  columns={columns}
                  columnIndex={index}
                  deleting={Boolean(deletingActionProps)}
                  minimizedFull={minimizedColumns.length === 4}
                  cardActions={{
                    openDialog: handleOpenDialog,
                    postponeAction: handlePostponeAction,
                    moveAction: onDragEnd,
                    // actionDone: handleActionDone,
                    // actionFail: handleActionFail,
                    deleteAction: handleDeleteDialogOpen
                  }}
                  handleColumnMinimize={handleColumnMinimize}
                  handleDialogClose={handleDialogClose}
                  handleAddNewCard={handleAddNewCard}
                />
              )
            })}
          </Grid>
        </DragDropContext>

        {shouldOpenDialog && (
          <AddOrEditActionDialog
            card={card}
            open={shouldOpenDialog}
            create={create}
            handleAddNewCard={handleAddNewCard}
            handleSave={handleActionSave}
            handleClose={handleDialogClose}
            isOnHold={isOnHold}
          />
        )}
      </div>
    </>

  )
}

const DeleteAllActionsButton = ({ orgId, handleDeleted }) => {
  const [deleting, setDeleting] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const actions = useSelector(state => {
    const all = []
    state.actionPlan.columns.forEach(column => {
      column.actions.forEach(action => all.push(action))
    })
    return all
  })
  const disabled = !orgId || actions.length === 0
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  return (
    <>
      <Button
        disabled={disabled}
        variant='outlined'
        color='primary'
        style={{
          zIndex: 10
        }}
        onClick={() => {
          setDialogOpen(true)
        }}
      >
        <Trans>ACTION_PLAN_DELETE_ALL_ACTIONS_BUTTON</Trans>
        <ButtonIcon IconClass={Delete} />
      </Button>
      <Dialog maxWidth='xs' fullWidth open={dialogOpen}>
        <DialogTitleWithIconClose
          label={<Trans>DELETE_ALL_ACTIONS_DIALOG_TITLE</Trans>}
          handleClose={e => {
            setDialogOpen(false)
          }}
          disabled={deleting}
        />
        <DialogContent sx={{ padding: '0 48px 32px 48px' }}>
          <Typography>
            <Trans>DELETE_ALL_ACTIONS_WARNING_TEXT</Trans>
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            padding: '0 48px 32px 48px',
            '& > :not(:first-of-type)': { ml: 0 }
          }}
        >
          <Button
            fullWidth
            variant='contained'
            color='primary'
            disabled={deleting}
            onClick={e => {
              setDeleting(true)
              const deletingKey = enqueueSnackbar(
                <Trans>DELETING_ALL_ACTIONS_SNACKBAR_IN_PROGRESS</Trans>,
                { persist: true, variant: 'info' }
              )
              queryDeleteObject({
                type: 'Action',
                ids: actions.map(action => action.id)
              }).then(
                result => {
                  setDeleting(false)
                  setDialogOpen(false)
                  closeSnackbar(deletingKey)
                  handleDeleted()
                  enqueueSnackbar(
                    <Trans>DELETING_ALL_ACTIONS_SNACKBAR_SUCCESS</Trans>,
                    { variant: 'success' }
                  )
                },
                reject => {
                  setDeleting(false)
                  setDialogOpen(false)
                  closeSnackbar(deletingKey)
                  enqueueSnackbar(
                    <Trans>DELETING_ALL_ACTIONS_SNACKBAR_ERROR</Trans>,
                    { persist: true, variant: 'error' }
                  )
                }
              )
            }}
          >
            <Trans>Yes</Trans>
          </Button>

          <Button
            fullWidth
            variant='outlined'
            color='primary'
            disabled={deleting}
            onClick={e => {
              setDialogOpen(false)
            }}
          >
            <Trans>No</Trans>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ActionPlan
